import React, { FunctionComponent, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "../ui/dialog";
import { Workout } from "./workoutsColumns";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { useOrg } from "@/lib/states/session/hooks";
import { trpc } from "@/lib/trpc/client";
import clsx from "clsx";
import { Separator } from "../ui/separator";
import { ScrollArea } from "../ui/scroll-area";
import { exercisesToExerciseCollection } from "@/lib/utils/WorkoutUtils";
import { v4 as uuidv4 } from "uuid";
import { Search, Dumbbell, Clock, Zap, Target, ArrowLeft } from "lucide-react";
import {
  DistanceUnit,
  formatDistance,
  formatTimeSeconds,
} from "@/lib/utils/exerciseTracking";

interface ImportWorkoutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onWorkoutSelected: (workout: Workout) => void;
}

const ImportWorkoutDialog: React.FC<ImportWorkoutDialogProps> = ({
  open,
  onOpenChange,
  onWorkoutSelected,
}) => {
  const org = useOrg();
  const distanceUnitLabel: DistanceUnit =
    org?.distanceUnitDefault === "mi" ? "mi" : "km";
  const [searchTerm, setSearchTerm] = useState("");
  const [showWorkoutDetails, setShowWorkoutDetails] = useState(false);

  const getSetTargetLabel = (trackingType: string | null, set: any) => {
    if (trackingType?.startsWith("distance")) {
      const distanceLabel = formatDistance(
        set.targetDistanceMeters,
        distanceUnitLabel,
        trackingType
      );
      const timeLabel = set.targetTimeSeconds
        ? ` in ${formatTimeSeconds(set.targetTimeSeconds)}`
        : "";
      return `${distanceLabel}${timeLabel}`.trim();
    }
    if (trackingType === "timed") {
      return formatTimeSeconds(set.targetTimeSeconds);
    }
    return `${set.startRange} - ${set.endRange} reps`;
  };

  const { data } = trpc.workouts.list.useQuery(undefined, {
    enabled: Boolean(open && org?.id),
  });
  type TemplateWorkout = NonNullable<typeof data>[number];
  const [selectedWorkout, setWorkout] = useState<TemplateWorkout | undefined>(
    data?.[0],
  );

  // Filter workouts based on search term
  const filteredWorkouts =
    data?.filter(
      (workout) =>
        workout.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        workout.description?.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

  const handleWorkoutSelect = (workout: TemplateWorkout) => {
    setWorkout(workout);
    // On mobile, show workout details view
    if (window.innerWidth < 768) {
      setShowWorkoutDetails(true);
    }
  };

  const handleBackToList = () => {
    setShowWorkoutDetails(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      analyticsName="import_workout_dialog"
    >
      <DialogContent className="max-w-6xl w-[95vw] h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 py-4 border-b">
          <h2 className="text-2xl font-bold text-foreground">
            Import Workout Template
          </h2>
          <p className="text-muted-foreground">
            Select a workout template to import into your library
          </p>
        </DialogHeader>

        <div className="flex flex-col md:flex-row h-full overflow-hidden">
          {/* Workouts List - Left Side (Desktop) / Full Width (Mobile) */}
          <div
            className={clsx(
              "border-r bg-muted/30",
              showWorkoutDetails ? "hidden md:block md:w-80" : "w-full md:w-80"
            )}
          >
            <div className="p-4 border-b">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search workouts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>

            <ScrollArea className="h-[calc(90vh-200px)] md:h-[calc(90vh-240px)]">
              <div className="p-2 space-y-2">
                {filteredWorkouts.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Dumbbell className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No workouts found</p>
                  </div>
                ) : (
                  filteredWorkouts.map((workout) => (
                    <Card
                      key={workout.id}
                      onClick={() => handleWorkoutSelect(workout)}
                      className={clsx(
                        "p-4 cursor-pointer transition-all duration-200 hover:shadow-md border-2 select-none",
                        workout.id === selectedWorkout?.id
                          ? "border-primary bg-primary/5 shadow-md"
                          : "border-border hover:border-primary/30"
                      )}
                    >
                      <div className="space-y-2">
                        <h3 className="font-semibold text-foreground line-clamp-1">
                          {workout.name}
                        </h3>
                        {workout.description && (
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {workout.description}
                          </p>
                        )}
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Target className="h-3 w-3" />
                          <span>
                            {workout.exercises?.length || 0} exercises
                          </span>
                        </div>
                      </div>
                    </Card>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Workout Details - Right Side (Desktop) */}
          <div className="hidden md:flex flex-1 flex-col min-h-0 overflow-hidden">
            {selectedWorkout ? (
              <>
                <div className="flex-shrink-0 p-6 border-b bg-gradient-to-r from-background to-muted/20">
                  <div className="space-y-3">
                    <h1 className="text-2xl font-bold text-foreground">
                      {selectedWorkout.name}
                    </h1>
                    {selectedWorkout.description && (
                      <p className="text-muted-foreground text-lg leading-relaxed line-clamp-3">
                        {selectedWorkout.description}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <Target className="h-4 w-4" />
                        <span>
                          {selectedWorkout.exercises?.length || 0} exercises
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        <span>Template</span>
                      </div>
                    </div>
                  </div>
                </div>

                <ScrollArea className="flex-1 min-h-0">
                  <div className="p-6 space-y-6">
                    {selectedWorkout.exercises?.map((exercise, index) => (
                      <div key={exercise.id} className="space-y-4 select-none">
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-sm font-semibold text-primary">
                            {index + 1}
                          </div>
                          <div className="flex-1 space-y-3">
                            <h3 className="text-lg font-semibold text-foreground">
                              {exercise.name}
                            </h3>

                            <div className="flex flex-wrap gap-3">
                              {exercise.tempo && (
                                <BadgeWithTitle
                                  title="Tempo"
                                  value={exercise.tempo}
                                  icon={<Clock className="h-3 w-3" />}
                                />
                              )}
                              {exercise.rest_time !== null &&
                                exercise.rest_time !== undefined && (
                                  <BadgeWithTitle
                                    title="Rest"
                                    value={`${exercise.rest_time}s`}
                                    icon={<Zap className="h-3 w-3" />}
                                  />
                                )}
                              {exercise.rir !== null &&
                                exercise.rir !== undefined && (
                                  <BadgeWithTitle
                                    title="RIR"
                                    value={exercise.rir.toString()}
                                    icon={<Target className="h-3 w-3" />}
                                  />
                                )}
                            </div>

                            {exercise.sets && exercise.sets.length > 0 && (
                              <div className="space-y-2">
                                <h4 className="text-sm font-medium text-muted-foreground">
                                  Sets:
                                </h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                                  {exercise.sets.map(
                                    (set: any, setIndex: number) => (
                                      <div
                                        key={setIndex}
                                        className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg"
                                      >
                                        <span className="w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center text-xs font-semibold text-primary">
                                          {setIndex + 1}
                                        </span>
                                        <span className="text-sm text-foreground">
                                          {getSetTargetLabel(
                                            exercise.tracking_type,
                                            set
                                          )}
                                        </span>
                                      </div>
                                    )
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {index !==
                          (selectedWorkout.exercises?.length || 0) - 1 && (
                          <Separator className="my-6" />
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center text-muted-foreground">
                  <Dumbbell className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">Select a workout to view details</p>
                </div>
              </div>
            )}
          </div>

          {/* Mobile Workout Details - Full Screen View */}
          <div
            className={clsx(
              "md:hidden flex-1 flex flex-col min-h-0 overflow-hidden",
              showWorkoutDetails ? "block" : "hidden"
            )}
          >
            {selectedWorkout && (
              <>
                {/* Mobile Header with Back Button */}
                <div className="flex-shrink-0 p-4 border-b bg-gradient-to-r from-background to-muted/20">
                  <div className="flex items-center gap-3 mb-3">
                    <button
                      onClick={handleBackToList}
                      className="p-2 hover:bg-muted/50 rounded-lg transition-colors"
                    >
                      <ArrowLeft className="h-5 w-5 text-foreground" />
                    </button>
                    <h1 className="text-xl font-bold text-foreground">
                      {selectedWorkout.name}
                    </h1>
                  </div>

                  {selectedWorkout.description && (
                    <p className="text-muted-foreground text-sm leading-relaxed line-clamp-3 ml-12">
                      {selectedWorkout.description}
                    </p>
                  )}

                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground ml-12 mt-2">
                    <div className="flex items-center gap-2">
                      <Target className="h-4 w-4" />
                      <span>
                        {selectedWorkout.exercises?.length || 0} exercises
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      <span>Template</span>
                    </div>
                  </div>
                </div>

                {/* Mobile Exercise List */}
                <ScrollArea className="flex-1 min-h-0">
                  <div className="p-4 space-y-4">
                    {selectedWorkout.exercises?.map((exercise, index) => (
                      <div
                        key={exercise.id}
                        className="space-y-3 p-4 bg-muted/20 rounded-lg select-none"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-sm font-semibold text-primary">
                            {index + 1}
                          </div>
                          <h3 className="text-lg font-semibold text-foreground">
                            {exercise.name}
                          </h3>
                        </div>

                        <div className="flex flex-wrap gap-2 ml-11">
                          {exercise.tempo && (
                            <BadgeWithTitle
                              title="Tempo"
                              value={exercise.tempo}
                              icon={<Clock className="h-3 w-3" />}
                            />
                          )}
                          {exercise.rest_time !== null &&
                            exercise.rest_time !== undefined && (
                              <BadgeWithTitle
                                title="Rest"
                                value={`${exercise.rest_time}s`}
                                icon={<Zap className="h-3 w-3" />}
                              />
                            )}
                          {exercise.rir !== null &&
                            exercise.rir !== undefined && (
                              <BadgeWithTitle
                                title="RIR"
                                value={exercise.rir.toString()}
                                icon={<Target className="h-3 w-3" />}
                              />
                            )}
                        </div>

                        {exercise.sets && exercise.sets.length > 0 && (
                          <div className="ml-11 space-y-2">
                            <h4 className="text-sm font-medium text-muted-foreground">
                              Sets:
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {exercise.sets.map(
                                (set: any, setIndex: number) => (
                                  <div
                                    key={setIndex}
                                    className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg"
                                  >
                                    <span className="w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center text-xs font-semibold text-primary">
                                      {setIndex + 1}
                                    </span>
                                    <span className="text-sm text-foreground">
                                      {getSetTargetLabel(
                                        exercise.tracking_type,
                                        set
                                      )}
                                    </span>
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </>
            )}
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t bg-muted/20">
          <div className="flex gap-3 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 sm:flex-none"
            >
              Cancel
            </Button>
            <Button
              disabled={!selectedWorkout}
              onClick={() => {
                if (selectedWorkout) {
                  const w: Workout = {
                    ...selectedWorkout,
                    id: uuidv4(), // New ID to duplicate
                    exerciseCollection: exercisesToExerciseCollection(
                      selectedWorkout.exercises
                    ),
                  };
                  onWorkoutSelected(w);
                  onOpenChange(false);
                }
              }}
              className="flex-1 sm:flex-none"
            >
              Import Workout
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface BadgeWithTitleProps {
  title: string;
  value: string;
  icon?: React.ReactNode;
}

const BadgeWithTitle: FunctionComponent<BadgeWithTitleProps> = ({
  title,
  value,
  icon,
}) => {
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-lg border border-border/50">
      {icon}
      <div className="flex flex-col">
        <span className="text-xs text-muted-foreground font-medium">
          {title}
        </span>
        <span className="text-sm font-semibold text-foreground">{value}</span>
      </div>
    </div>
  );
};

export default ImportWorkoutDialog;
